/**
 * ProviderEarningsPage — `/dashboard/provider/earnings`.
 *
 * Phase 2 extraction: the lifetime/pending earnings card +
 * ProviderCreditCard (PooBucks-equivalent display) +
 * StripeConnectCard (Phase 3a status panel). Pulls completed +
 * in-progress bookings to compute earnings totals.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import StripeConnectCard from "@/components/dashboard/StripeConnectCard";
import CreditHistoryDrawer from "@/components/CreditHistoryDrawer";
import { Lock } from "lucide-react";

/**
 * Last30DaysTile — trailing-30-day commissionable volume.
 *
 * Reads `GET /api/profiles/provider/earnings-window`, which delegates
 * to the SAME `compute_provider_window_volume()` helper that powers
 * Item C's `/tier-upgrade-estimate` endpoint. One aggregation, two
 * consumers — see `tier_upgrade_requests.py`. The number rendered
 * here EQUALS the `current_window_volume_cents` field on the upgrade
 * estimate; that contract is asserted by the source-equivalence
 * backend test.
 *
 * Neutral framing guardrail:
 *   - No comparison arrows, no % deltas, no down-trend markup.
 *   - Insufficient history (< 3 completed bookings in window) renders
 *     "Not enough activity yet" — NOT "$0", which a partner could
 *     misread as a loss. Threshold lives in the backend helper.
 *   - Same Card styling as the existing lifetime/credit tiles.
 *     NO conditional color variant — slow months look the same as
 *     busy months structurally; only the number differs.
 *   - No sparkline / chart / trend element — scope-locked for this
 *     dispatch. Add later only if engagement justifies it.
 */
export function Last30DaysTile() {
  const [data, setData] = useState(null); // null = loading, {} = loaded
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await api.get("/profiles/provider/earnings-window");
        if (active) setData(resp.data || null);
      } catch (e) {
        if (active) {
          setErrored(true);
          // Opportunistic tile — silently degrade. A toast here would
          // be noisy because the page has multiple network reads
          // running in parallel.
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <Card
      className="mb-6 rounded-3xl border-border bg-pp-surface2 p-6"
      data-testid="provider-window-tile"
      style={{ boxShadow: "var(--pp-card-shadow)" }}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-pp-text-muted">
        Last 30 days
      </p>
      {data === null && !errored && (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="provider-window-tile-loading"
        >
          Loading…
        </p>
      )}
      {errored && (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="provider-window-tile-error"
        >
          Window data unavailable right now.
        </p>
      )}
      {data && data.has_sufficient_history && (
        <>
          <p
            className="mt-1 font-heading text-4xl font-bold text-pp-text sm:text-5xl"
            data-testid="provider-window-tile-value"
          >
            ${((data.current_window_volume_cents || 0) / 100).toFixed(2)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Commissionable booking volume across the last{" "}
            {data.window_days || 30} days (completed bookings).
          </p>
        </>
      )}
      {data && !data.has_sufficient_history && (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="provider-window-tile-insufficient"
        >
          Not enough activity yet — completed bookings will start showing
          up here once you’ve served a few customers.
        </p>
      )}
    </Card>
  );
}

function ProviderCreditCard() {
  const { creditLabel } = useConfig();
  const { providerProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const balance = Number(providerProfile?.credit_balance || 0);
  return (
    <>
      <Card
        className="mb-6 rounded-3xl border-border bg-pp-surface2 p-6"
        data-testid="provider-credit-card"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-pp-text-muted">
              {creditLabel.toUpperCase()}
            </p>
            <p
              className="mt-1 font-heading text-4xl font-bold text-brand-orange sm:text-5xl"
              data-testid="provider-credit-balance"
            >
              ${balance.toFixed(2)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Applied automatically to future payouts (coming soon).
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 rounded-full border border-pp-border px-4 py-2 text-xs font-semibold text-pp-text transition hover:bg-pp-surface"
            data-testid="provider-credit-history-button"
          >
            View history
          </button>
        </div>
      </Card>
      <CreditHistoryDrawer
        open={open}
        onOpenChange={setOpen}
        creditLabel={creditLabel}
        currentBalance={balance}
      />
    </>
  );
}

export default function ProviderEarningsPage() {
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // `view=provider` disambiguates perspective for multi-cap
        // (tri-role) users so the earnings tile always reads the
        // provider-side rows. Idempotent for pure-provider accounts.
        // See /app/memory/bookings_mine_tri_role_plan.md.
        const { data } = await api.get("/bookings/mine", { params: { view: "provider" } });
        if (active) setBookings(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const completed = useMemo(
    () => bookings.filter((b) => b.status === "completed"),
    [bookings]
  );
  const inProgress = useMemo(
    () => bookings.filter((b) => b.status === "in_progress"),
    [bookings]
  );
  const lifetimeEarnings = useMemo(
    () =>
      completed.reduce(
        (s, b) => s + (b.pricing_snapshot?.provider_payout || 0),
        0
      ),
    [completed]
  );
  const pendingEarnings = useMemo(
    () =>
      inProgress.reduce(
        (s, b) => s + (b.pricing_snapshot?.provider_payout || 0),
        0
      ),
    [inProgress]
  );

  return (
    <div className="mx-auto max-w-6xl" data-testid="provider-earnings-page">
      <header className="mb-6">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Earnings
        </span>
        <h2 className="font-heading text-3xl font-bold text-pp-text">
          Payouts &amp; balance
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Track lifetime earnings, pending payouts, and credits.
        </p>
      </header>

      <Card
        className="mb-6 rounded-3xl border-border bg-secondary p-8 shadow-none"
        data-testid="provider-earnings-card"
      >
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-pp-text">
              Lifetime earnings
            </p>
            <p
              className="mt-2 font-heading text-5xl font-bold text-pp-text sm:text-6xl"
              data-testid="provider-earnings-display"
            >
              ${lifetimeEarnings.toFixed(2)}
            </p>
            {pendingEarnings > 0 && (
              <p
                className="mt-1 text-sm font-semibold text-brand-orange"
                data-testid="provider-earnings-pending"
              >
                + ${pendingEarnings.toFixed(2)} pending
              </p>
            )}
            <p className="mt-1 text-sm text-muted-foreground">
              Connect with Stripe to receive payouts (coming soon).
            </p>
          </div>

          {/* MOCKED: Stripe Connect onboarding — at launch */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button
                    type="button"
                    disabled
                    title="Coming soon"
                    aria-label="Connect with Stripe — coming soon"
                    className="h-12 cursor-not-allowed rounded-full bg-brand-orange/40 px-6 text-white opacity-80"
                    data-testid="provider-stripe-connect-button"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    Connect with Stripe
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="rounded-2xl"
                data-testid="provider-stripe-tooltip"
              >
                Coming soon
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </Card>

      <div className="mb-6">
        <StripeConnectCard />
      </div>

      <Last30DaysTile />

      <ProviderCreditCard />
    </div>
  );
}
